
  import { defineComponent, ref, onMounted } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { toHtmlDocument } from '@/core/helpers/htmlDocument';
  import { useI18n } from 'vue-i18n';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2';
  import Editor from '@tinymce/tinymce-vue';
  import { OrganizerObject } from '@/store/modules/Auctions/OrganizersModule';

  export default defineComponent({
    name: 'organizers-descriptions-web-editing',
    components: {
      editor: Editor,
      Form,
    },
    setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const { can } = useAbility();
      const submitButton = ref<HTMLButtonElement | null>(null);

      const translate = (text: string) => (te(text) ? t(text) : text);

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('organizerDescriptionsUpdating'), [
          translate('organizers'),
        ]);
      });

      const organizer = ref<OrganizerObject>({} as OrganizerObject);
      const htmlAr = ref('');
      const htmlEn = ref('');

      const fetchOrganizer = async () => {
        try {
          const { data } = await store.dispatch(
            Actions.GET_ORGANIZER,
            route.params.id
          );
          organizer.value = data.data;
          htmlAr.value = organizer.value.descArWeb;
          htmlEn.value = organizer.value.descEnWeb;
        } catch (error) {
          Swal.fire({
            text: translate('FAILED_TO_FETCH_ORGANIZER'),
            icon: 'error',
          }).then(() => {
            router.push({ name: 'organizers-listing' });
          });
        }
      };

      fetchOrganizer();

      const goBack = () => {
        router.back();
      };

      const onSubmitUpdate = async () => {
        if (!organizer.value.id) return;

        if (submitButton.value) {
          submitButton.value.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        try {
          await EditorChangeAr(htmlAr.value);
          await EditorChangeEn(htmlEn.value);

          await store.dispatch(Actions.UPDATE_ORGANIZER, {
            id: organizer.value.id,
            data: {
              descArWeb: htmlAr.value,
              descEnWeb: htmlEn.value,
            },
          });

          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_ORGANIZER_DESCRIPTIONS'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: { confirmButton: 'btn btn-light-primary' },
          }).then(() => {
            router.push({ name: 'organizers-listing' });
          });
        } catch (error) {
          Swal.fire({
            text: translate('UPDATE_FAILED'),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: { confirmButton: 'btn fw-bold btn-light-danger' },
          });
        } finally {
          if (submitButton.value) {
            submitButton.value.disabled = false;
            submitButton.value.removeAttribute('data-kt-indicator');
          }
        }
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      const EditorChangeAr = async (newContent) => {
        htmlAr.value = toHtmlDocument(newContent, 'rtl');
      };

      const EditorChangeEn = async (newContent) => {
        htmlEn.value = toHtmlDocument(newContent, 'ltr');
      };

      return {
        translate,
        goBack,
        submitButton,
        onSubmitUpdate,
        htmlEn,
        htmlAr,
        can,
        organizer,
      };
    },
  });
